import { Component, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, AlertCircle } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  errorType: "connection" | "stale_deployment" | "unknown" | null;
  errorMessage: string;
  retryCount: number;
  isRetrying: boolean;
}

const MAX_AUTO_RETRIES = 2;
const RETRY_DELAY_MS = 1500;

export class ConvexErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorType: null, errorMessage: "", retryCount: 0, isRetrying: false };
  }

  static getDerivedStateFromError(error: Error & { code?: string; data?: { code?: string } }): Partial<State> {
    const rawMessage = error.message || "Unknown error";
    const message = rawMessage.toLowerCase();

    // Stale deployment — lazy-loaded chunks have new filenames after a deploy.
    // The old cached index.js references filenames that no longer exist (404).
    // Only a full page reload (which fetches new index.html) can fix this.
    if (
      message.includes("dynamically imported module") ||
      message.includes("loading chunk") ||
      message.includes("loading css chunk")
    ) {
      console.warn("[ConvexErrorBoundary] Stale deployment detected:", rawMessage);
      return { hasError: true, errorType: "stale_deployment", errorMessage: rawMessage };
    }

    if (
      message.includes("network") ||
      message.includes("connection") ||
      message.includes("failed to fetch") ||
      message.includes("timeout") ||
      message.includes("socket") ||
      message.includes("websocket") ||
      message.includes("disconnected") ||
      message.includes("could not connect")
    ) {
      return { hasError: true, errorType: "connection", errorMessage: rawMessage };
    }

    return { hasError: true, errorType: "unknown", errorMessage: rawMessage };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[ConvexErrorBoundary] Caught error:", error.message);
    console.error("[ConvexErrorBoundary] Error details:", error);
    console.error("[ConvexErrorBoundary] JS stack trace:", error.stack);
    console.error("[ConvexErrorBoundary] Component stack:", errorInfo.componentStack);

    // Stale deployment: auto-reload to get new build. Guard against infinite loop.
    if (this.state.errorType === "stale_deployment") {
      const lastReload = sessionStorage.getItem("stale_deploy_reload");
      const now = Date.now();
      if (!lastReload || now - Number(lastReload) > 30000) {
        sessionStorage.setItem("stale_deploy_reload", String(now));
        console.log("[ConvexErrorBoundary] Stale deployment — reloading to get new build");
        window.location.reload();
      } else {
        console.log("[ConvexErrorBoundary] Stale deployment — already reloaded recently, showing error UI");
      }
      return;
    }

    // Only auto-retry genuine connection errors (network/WebSocket issues).
    // Do NOT auto-retry "unknown" errors — they may be server bugs or disguised
    // auth errors that will never self-resolve, causing crash loops.
    if (
      this.state.errorType === "connection" &&
      this.state.retryCount < MAX_AUTO_RETRIES
    ) {
      console.log(`[ConvexErrorBoundary] Auto-retry ${this.state.retryCount + 1}/${MAX_AUTO_RETRIES}`);
      this.scheduleRetry();
    }
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  scheduleRetry = () => {
    this.setState({ isRetrying: true });

    this.retryTimeoutId = setTimeout(() => {
      this.setState((prevState) => ({
        hasError: false,
        errorType: null,
        errorMessage: "",
        retryCount: prevState.retryCount + 1,
        isRetrying: false,
      }));
    }, RETRY_DELAY_MS);
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      errorType: null,
      errorMessage: "",
      retryCount: this.state.retryCount + 1,
      isRetrying: false,
    });
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.state.errorType === "stale_deployment") {
        return (
          <div className="flex items-center justify-center min-h-screen bg-background">
            <div className="text-center p-6 max-w-sm">
              <RefreshCw className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2">App Updated</h2>
              <p className="text-muted-foreground mb-6">
                A new version has been deployed. Please refresh to continue.
              </p>
              <Button
                onClick={this.handleRefresh}
                data-testid="button-refresh-app"
                className="w-full"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh App
              </Button>
            </div>
          </div>
        );
      }

      if (this.state.isRetrying) {
        return (
          <div className="flex items-center justify-center min-h-screen bg-background">
            <div className="text-center p-6">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">Reconnecting...</p>
            </div>
          </div>
        );
      }

      if (this.state.errorType === "connection") {
        return this.props.fallback || (
          <div className="flex items-center justify-center min-h-screen bg-background">
            <div className="text-center p-6 max-w-sm">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
              <h2 className="text-xl font-semibold mb-2">Connection Lost</h2>
              <p className="text-muted-foreground mb-6">
                Unable to connect to the server. This often happens when returning to the app after a while.
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={this.handleRefresh}
                  data-testid="button-refresh-app"
                  className="w-full"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh App
                </Button>
                <Button
                  variant="outline"
                  onClick={this.handleRetry}
                  data-testid="button-retry"
                  className="w-full"
                >
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        );
      }

      // "unknown" errors — show error UI without auto-retry.
      return this.props.fallback || (
        <div className="flex items-center justify-center min-h-screen bg-background">
          <div className="text-center p-6 max-w-sm">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
            <p className="text-muted-foreground mb-2">An error occurred while loading data.</p>
            {this.state.errorMessage && (
              <p className="text-xs text-muted-foreground/60 mb-4 max-w-xs mx-auto break-words font-mono">{this.state.errorMessage.slice(0, 200)}</p>
            )}
            <div className="flex flex-col gap-3">
              <Button
                onClick={this.handleRefresh}
                data-testid="button-refresh-app"
                className="w-full"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh App
              </Button>
              <Button
                variant="outline"
                onClick={this.handleRetry}
                data-testid="button-retry"
                className="w-full"
              >
                Try Again
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
